// @givmo/board — a seat's terms, the board's term rules and classes, and the annual meeting's
// suggestion, read tolerantly (CBG-YR3-TERMS, -ELECT, -ANNUAL; the backend's contract
// `agent_docs/contracts/board-terms.md` §5, §8).
//
// What the wire says, and what this module only DISPLAYS (§11.1; nothing here is stored or sent):
//
//   * `terms[]` is a seat's terms, the CURRENT one first (the latest start, a missing start lowest —
//     the backend orders them). A seat with no term has an unrecorded term, never an expired one.
//   * A current term whose recorded end has passed while the seat still serves is a HOLDOVER, said in
//     the approved words ("Term ended … – still serving"); the person still serves, votes and counts.
//     Compared as calendar days against the reader's own (`todayAsDateInput`), never as instants.
//   * Consecutive terms are the seat's terms the board marked as counting toward its limit: a seat
//     serves continuously (removal is terminal), so they are consecutive by construction (§11.1).
//   * A suggested end is the day before the start's anniversary the board's recorded number of years
//     on — the backend's own rule (`board_terms.end_after_years`): February 29's anniversary in a
//     common year is March 1, so such a term ends February 28. A prefill, labeled with its source.
//   * The term rules are the board's own cited parameters; with nothing recorded nothing is derived.

import { extractList } from '@givmo/api-client/list';
import type {
  BoardDirectorClassOut,
  BoardMemberTermOut,
  BoardSuggestedDirectorOut,
  BoardSuggestedOfficerOut,
  BoardTermSuggestionsOut,
  OrgBoardGovernanceParameterOut,
  OrgBoardMemberOut,
} from '@givmo/api-client';

const CALENDAR_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const text = (value: unknown): string | null =>
  typeof value === 'string' && value.trim().length > 0 ? value : null;

const calendarDay = (value: unknown): string | null =>
  typeof value === 'string' && CALENDAR_DATE.test(value) ? value : null;

// ── A seat's terms ───────────────────────────────────────────────────────────────────────────────

/** The seat's terms, current first, as served; `[]` for an answer that carries none. */
export function seatTerms(seat: OrgBoardMemberOut | null | undefined): BoardMemberTermOut[] {
  const raw = (seat as { terms?: unknown } | null | undefined)?.terms;
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (term): term is BoardMemberTermOut => isRecord(term) && typeof term.id === 'string',
  );
}

/** The seat's current term, or `null` when it has none. */
export function currentTerm(seat: OrgBoardMemberOut | null | undefined): BoardMemberTermOut | null {
  return seatTerms(seat)[0] ?? null;
}

/** The current term's recorded end, when it is before `today` and the seat still serves. */
export function heldOverEnd(
  term: BoardMemberTermOut | null,
  seat: OrgBoardMemberOut,
  today: string,
): string | null {
  const end = calendarDay(term?.ends_on);
  if (end === null || seat.status !== 'active' || calendarDay(today) === null) return null;
  return end < today ? end : null;
}

/** How many of the seat's terms the board counts toward its limit. */
export function countedTerms(seat: OrgBoardMemberOut | null | undefined): number {
  return seatTerms(seat).filter((term) => term.counts_toward_limit !== false).length;
}

// ── The board's term rules (§4) ──────────────────────────────────────────────────────────────────

export const TERM_RULE_KINDS = [
  'director_term_length',
  'director_term_limit',
  'officer_term_length',
] as const;
export type TermRuleKind = (typeof TERM_RULE_KINDS)[number];

/** Whether a parameter is one of the three term rules (which Meeting rules leaves to the Terms tab). */
export function isTermRuleKind(kind: string | null | undefined): kind is TermRuleKind {
  return (TERM_RULE_KINDS as readonly string[]).includes((kind ?? '').toLowerCase());
}

/** Each rule's own basis, as the backend holds it. */
export const TERM_RULE_BASIS: Readonly<Record<TermRuleKind, 'years' | 'terms'>> = {
  director_term_length: 'years',
  director_term_limit: 'terms',
  officer_term_length: 'years',
};

export interface TermRule {
  kind: TermRuleKind;
  /** The count the board recorded. */
  value: number;
  /** The backend's own words for it ("3 years"), echoed. */
  summary: string;
  citation: string;
  note: string | null;
  recordedAt: string | null;
  /** The rows it superseded, newest first. */
  superseded: OrgBoardGovernanceParameterOut[];
}

const recordedAt = (row: OrgBoardGovernanceParameterOut) =>
  new Date(row.recorded_at ?? 0).getTime() || 0;

/** The board's rule of one term kind in force, or `null` with none recorded. */
export function termRule(
  parameters: readonly OrgBoardGovernanceParameterOut[],
  kind: TermRuleKind,
): TermRule | null {
  const rows = parameters.filter((row) => (row.parameter_kind ?? '').toLowerCase() === kind);
  const active = rows.find((row) => (row.status ?? '').toUpperCase() === 'ACTIVE');
  if (active === undefined) return null;
  const value = Number(active.count_value);
  if (!Number.isInteger(value) || value < 1) return null;
  return {
    kind,
    value,
    summary: active.value_summary ?? String(value),
    citation: active.bylaw_citation ?? '',
    note: text(active.note),
    recordedAt: text(active.recorded_at),
    superseded: rows
      .filter((row) => row !== active)
      .sort((left, right) => recordedAt(right) - recordedAt(left)),
  };
}

// ── Calendar arithmetic (display windows and prefills only) ──────────────────────────────────────

function parts(day: string): [number, number, number] | null {
  const match = CALENDAR_DATE.exec(day);
  return match === null ? null : [Number(match[1]), Number(match[2]), Number(match[3])];
}

const pad = (value: number) => String(value).padStart(2, '0');
const asDay = (year: number, month: number, day: number) => `${year}-${pad(month)}-${pad(day)}`;
const daysIn = (year: number, month: number) => new Date(year, month, 0).getDate();

/** `day` plus `months` calendar months, its day of the month clamped to the month's end (the
 *  backend's `months_after`). `null` for anything but a calendar day. */
export function addMonths(day: string, months: number): string | null {
  const read = parts(day);
  if (read === null) return null;
  const [year, month, date] = read;
  const index = month - 1 + months;
  const nextYear = year + Math.floor(index / 12);
  const nextMonth = (((index % 12) + 12) % 12) + 1;
  return asDay(nextYear, nextMonth, Math.min(date, daysIn(nextYear, nextMonth)));
}

/** A `years`-year term's last day from `start`: the day before the anniversary, February 29's
 *  anniversary in a common year being March 1 (the backend's `end_after_years`). */
export function endAfterYears(start: string, years: number): string | null {
  const read = parts(start);
  if (read === null || !Number.isInteger(years) || years < 1) return null;
  const [year, month, day] = read;
  const anniversary =
    month === 2 && day === 29 && daysIn(year + years, 2) === 28
      ? new Date(year + years, 2, 1)
      : new Date(year + years, month - 1, day);
  anniversary.setDate(anniversary.getDate() - 1);
  return asDay(anniversary.getFullYear(), anniversary.getMonth() + 1, anniversary.getDate());
}

// ── The board's classes (§5.3) ───────────────────────────────────────────────────────────────────

/** The classes as served (active first in the board's order, then retired); `[]` on any other shape. */
export function extractDirectorClasses(body: unknown): BoardDirectorClassOut[] {
  return extractList<BoardDirectorClassOut>(body, 'data').filter(
    (row) => isRecord(row) && typeof row.id === 'string' && typeof row.label === 'string',
  );
}

export function activeClasses(classes: readonly BoardDirectorClassOut[]): BoardDirectorClassOut[] {
  return classes.filter((row) => row.status !== 'retired');
}

// ── The annual meeting's suggestion (§8.1) ───────────────────────────────────────────────────────

export interface TermSuggestions {
  offered: boolean;
  windowEndsOn: string | null;
  dismissedAt: string | null;
  addedAt: string | null;
  directors: BoardSuggestedDirectorOut[];
  officers: BoardSuggestedOfficerOut[];
  officerTerm: { years: number; citation: string } | null;
  addedItemIds: string[];
  templateSections: { directors: string | null; officers: string | null };
}

const NO_SUGGESTIONS: TermSuggestions = {
  offered: false,
  windowEndsOn: null,
  dismissedAt: null,
  addedAt: null,
  directors: [],
  officers: [],
  officerTerm: null,
  addedItemIds: [],
  templateSections: { directors: null, officers: null },
};

const rowsWithEnd = <Row>(value: unknown): Row[] =>
  Array.isArray(value)
    ? (value.filter(
        (row) =>
          isRecord(row) &&
          typeof row.board_member_id === 'string' &&
          calendarDay(row.ends_on) !== null,
      ) as Row[])
    : [];

/** The suggestion read, tolerantly: nothing offered on any shape this build doesn't know. */
export function extractTermSuggestions(body: unknown): TermSuggestions {
  if (!isRecord(body)) return NO_SUGGESTIONS;
  const read = body as Partial<BoardTermSuggestionsOut>;
  const sections = isRecord(read.template_sections) ? read.template_sections : {};
  const officerTerm = isRecord(read.officer_term) ? read.officer_term : null;
  return {
    offered: read.offered === true,
    windowEndsOn: calendarDay(read.window_ends_on),
    dismissedAt: text(read.dismissed_at),
    addedAt: text(read.added_at),
    directors: rowsWithEnd<BoardSuggestedDirectorOut>(read.directors),
    officers: rowsWithEnd<BoardSuggestedOfficerOut>(read.officers),
    officerTerm:
      officerTerm !== null && Number.isInteger(officerTerm.years)
        ? { years: Number(officerTerm.years), citation: String(officerTerm.bylaw_citation ?? '') }
        : null,
    addedItemIds: Array.isArray(read.added_item_ids)
      ? read.added_item_ids.filter((id): id is string => typeof id === 'string')
      : [],
    templateSections: {
      directors: text(sections.directors),
      officers: text(sections.officers),
    },
  };
}
